import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Countdown from 'react-countdown';
import { 
  Download, 
  Eye, 
  EyeOff, 
  Lock, 
  Clock, 
  FileText, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Copy,
  RefreshCw,
  Upload
} from 'lucide-react';

import { downloadAPI, handleAPIError } from '../utils/api';
import { 
  formatFileSize, 
  getFileIcon, 
  getFileCategory, 
  formatRelativeTime, 
  copyToClipboard, 
  getShareUrl 
} from '../utils/format';

export function Share() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  
  // Load file info
  useEffect(() => {
    const loadFileInfo = async () => {
      try {
        setLoading(true);
        const response = await downloadAPI.getFileInfo(shareId);
        setFileInfo(response.data);
        setIsPasswordVerified(!response.data.requiresPassword);
      } catch (error) {
        let errorMessage = handleAPIError(error);
        
        // Provide more specific error messages
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'The request timed out. The server may be slow to respond. Please try refreshing the page.';
        } else if (error.response?.status === 404) {
          errorMessage = 'File not found. This link may be invalid or the file may have been deleted.';
        } else if (error.response?.status === 410) {
          errorMessage = 'This file has expired and is no longer available for download.';
        }
        
        setError(errorMessage);
        
        // Redirect to home after 5 seconds for certain errors
        if (error.response?.status === 404 || error.response?.status === 410) {
          setTimeout(() => navigate('/'), 5000);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (shareId) {
      loadFileInfo();
    }
  }, [shareId, navigate]);
  
  // Verify password
  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }
    
    try {
      setVerifying(true);
      const response = await downloadAPI.verifyPassword(shareId, password);
      
      if (response.data.valid) {
        setIsPasswordVerified(true);
        toast.success('Password verified successfully!');
      } else {
        toast.error('Invalid password');
        setPassword('');
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast.error(errorMessage);
      setPassword('');
    } finally {
      setVerifying(false);
    }
  };
  
  // Download file with progress tracking
  const handleDownload = async () => {
    try {
      setDownloading(true);
      setDownloadProgress(0);
      setDownloadStatus('Preparing download...');
      
      await downloadAPI.downloadFile(
        shareId, 
        fileInfo.requiresPassword ? password : null,
        (progress, loaded, total) => {
          setDownloadProgress(progress);
          if (progress < 100) {
            setDownloadStatus(`Downloading... ${progress}% (${formatFileSize(loaded)} / ${formatFileSize(total)})`);
          } else {
            setDownloadStatus('Download complete!');
          }
        }
      );
      
      toast.success('File downloaded successfully!');
      
      // Refresh file info to update download count
      const updatedInfo = await downloadAPI.getFileInfo(shareId);
      setFileInfo(updatedInfo.data);
      
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast.error(`Download failed: ${errorMessage}`);
      setDownloadStatus('Download failed');
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      setTimeout(() => setDownloadStatus(''), 3000);
    }
  };
  
  // Copy share URL
  const handleCopyUrl = async () => {
    try {
      await copyToClipboard(getShareUrl(shareId));
      toast.success('Share URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };
  
  // Countdown renderer
  const countdownRenderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return (
        <div className="text-center py-8">
          <div className="mx-auto w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-danger-600" />
          </div>
          <h2 className="text-2xl font-bold text-danger-900 mb-2">File Expired</h2>
          <p className="text-danger-700">This file has expired and is no longer available for download.</p>
        </div>
      );
    }
    
    return (
      <div className="bg-primary-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-primary-600" />
            <span className="text-sm font-medium text-primary-700">Expires in:</span>
          </div>
          <div className="flex items-center space-x-2 text-lg font-bold text-primary-900">
            {days > 0 && <span>{days}d</span>}
            <span>{hours.toString().padStart(2, '0')}h</span>
            <span>{minutes.toString().padStart(2, '0')}m</span>
            <span>{seconds.toString().padStart(2, '0')}s</span>
          </div>
        </div>
      </div>
    );
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading...</h2>
          <p className="text-gray-600 mb-4">Retrieving file information...</p>
          <div className="text-sm text-gray-500">
            <p>This may take a few moments if the server is starting up.</p>
            <p className="mt-2">If this takes longer than 30 seconds, please refresh the page.</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-danger-600" />
          </div>
          <h2 className="text-2xl font-bold text-danger-900 mb-2">Error</h2>
          <p className="text-danger-700 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn btn-primary"
            >
              Go to Upload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Password verification required
  if (fileInfo && fileInfo.requiresPassword && !isPasswordVerified) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-warning-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Password Required</h1>
            <p className="text-gray-600">This file is password protected. Please enter the password to access it.</p>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl">{getFileIcon(fileInfo.mimeType)}</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 truncate">{fileInfo.filename}</h3>
                <p className="text-sm text-gray-500">
                  {formatFileSize(fileInfo.size)} • {getFileCategory(fileInfo.mimeType)}
                </p>
              </div>
              <div className="flex items-center space-x-1 text-sm text-gray-500">
                <Shield className="h-4 w-4" />
                <span>Protected</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleVerifyPassword()}
                    placeholder="Enter password"
                    className="input pr-10"
                    disabled={verifying}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <button
                  onClick={handleVerifyPassword}
                  disabled={verifying || !password.trim()}
                  className="btn btn-primary"
                >
                  {verifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Main download interface
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Download className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Download File</h1>
                <p className="text-primary-100">Secure file download</p>
              </div>
            </div>
            <button
              onClick={handleCopyUrl}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Copy share link"
            >
              <Copy className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8">
          {/* Expiration Countdown */}
          {fileInfo && (
            <Countdown
              date={new Date(fileInfo.expiresAt)}
              renderer={countdownRenderer}
            />
          )}
          
          {/* File Information */}
          {fileInfo && (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <div className="text-4xl">{getFileIcon(fileInfo.mimeType)}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{fileInfo.filename}</h3>
                  <p className="text-gray-600 mb-3">
                    {formatFileSize(fileInfo.size)} • {getFileCategory(fileInfo.mimeType)}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Download className="h-4 w-4" />
                      <span>{fileInfo.downloadCount} downloads</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>Uploaded {formatRelativeTime(fileInfo.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Progress */}
              {downloading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                    <span className="text-sm font-medium text-blue-800">{downloadStatus}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${downloadProgress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-blue-600 mt-1 text-right">{downloadProgress}%</div>
                </div>
              )}
              
              {/* Download Limits */}
              {fileInfo.maxDownloads && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        Download Limit: {fileInfo.downloadCount} / {fileInfo.maxDownloads}
                      </p>
                      <p className="text-sm text-yellow-700">
                        {fileInfo.maxDownloads - fileInfo.downloadCount} downloads remaining
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Security Info */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Secure Download</p>
                    <p className="text-sm text-green-700">
                      {fileInfo.requiresPassword ? 'Password protected' : 'No password required'}
                    </p>
                  </div>
                </div>
                <div className="text-sm text-green-600 font-medium">
                  Expires {formatRelativeTime(fileInfo.expiresAt)}
                </div>
              </div>
              
              {/* Download Actions */}
              <div className="space-y-4">
                {/* Main Download Button */}
                <div className="text-center">
                  <button
                    onClick={handleDownload}
                    disabled={downloading || (fileInfo.maxDownloads && fileInfo.downloadCount >= fileInfo.maxDownloads)}
                    className="btn btn-primary btn-lg w-full sm:w-auto"
                  >
                    {downloading ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Download {fileInfo.filename}
                      </>
                    )}
                  </button>
                </div>

                {/* Additional Actions */}
                <div className="flex justify-center space-x-3 text-sm">
                  <button
                    onClick={handleCopyUrl}
                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copy Link</span>
                  </button>
                  <span className="text-gray-300">•</span>
                  <button
                    onClick={() => navigate('/')}
                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Upload Files</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 